import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import { Save, Stethoscope, ImagePlus, X, Globe, MapPin, QrCode, Copy, Plus, BadgeCheck, ShieldCheck, Clock, AlertCircle } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import { fileToCompressedDataUrl } from "../lib/localPhotos";
import { downloadProfileQrPoster } from "../lib/pdf";
import { SPECIALTIES } from "../lib/specialties";
import { QRCodeCanvas } from "qrcode.react";

const Field = ({ label, k, placeholder, value, onChange }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">{label}</Label>
    <Input data-testid={`profile-${k}`} value={value} placeholder={placeholder}
      onChange={onChange} className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
  </div>
);

const FIELDS = [
  ["Nombre para receta", "name", "Dr. Néstor Gibran López Díaz"],
  ["Cédula profesional", "cedula", "0000000"],
  ["Universidad / Escuela", "university", "UNAM, Facultad de Medicina"],
  ["RFC", "rfc", "XXXX000000XXX"],
  ["Teléfono", "phone", "55 0000 0000"],
  ["Nombre del consultorio / clínica", "clinic_name", "Consultorio Pediátrico"],
];

export default function DoctorProfile() {
  const { user, setUser } = useAuth();
  const initSpecialties = (user?.specialties && user.specialties.length)
    ? user.specialties.map((s) => ({ name: s.name || "", cedula: s.cedula || "" }))
    : [{ name: user?.specialty || "Pediatría", cedula: user?.specialty_cedula || "" }];
  const [specialties, setSpecialties] = useState(initSpecialties);
  const [cedulas, setCedulas] = useState(user?.cedulas || []);
  const [form, setForm] = useState({
    name: user?.name || "", specialty: user?.specialty || "Pediatría",
    cedula: user?.cedula || "", specialty_cedula: user?.specialty_cedula || "",
    university: user?.university || "", rfc: user?.rfc || "",
    clinic_name: user?.clinic_name || "",
    phone: user?.phone || "", address: user?.address || "",
    signature: user?.signature || "",
    logos: user?.logos || [],
    prescription_format: user?.prescription_format || "completa",
    photo: user?.photo || "",
    service_areas: user?.service_areas || [],
    catalog_bio: user?.catalog_bio || "",
    public_listed: user?.public_listed || false,
    profile_type: user?.profile_type || "medico",
  });
  const [saving, setSaving] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const vStatus = user?.verification_status || "none";
  const [vForm, setVForm] = useState({
    name: user?.name || "", cedula: user?.cedula || user?.verification_cedula || "",
    profession: user?.specialty || "", specialty_cedula: user?.specialty_cedula || "",
  });
  const [vSaving, setVSaving] = useState(false);
  const requestVerification = async () => {
    if (!vForm.name.trim() || !vForm.cedula.trim()) { toast.error("Nombre y número de cédula son obligatorios"); return; }
    setVSaving(true);
    try {
      const res = await api.post("/verification/request", vForm);
      setUser(res.data);
      toast.success("¡Solicitud enviada! Revisaremos tu cédula pronto.");
    } catch { toast.error("No se pudo enviar la solicitud"); }
    finally { setVSaving(false); }
  };

  const setSpec = (i, key, val) => setSpecialties((arr) => arr.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addSpec = () => setSpecialties((arr) => [...arr, { name: "", cedula: "" }]);
  const removeSpec = (i) => setSpecialties((arr) => arr.filter((_, idx) => idx !== i));
  const setCed = (i, val) => setCedulas((arr) => arr.map((c, idx) => (idx === i ? val : c)));
  const addCed = () => setCedulas((arr) => [...arr, ""]);
  const removeCed = (i) => setCedulas((arr) => arr.filter((_, idx) => idx !== i));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToCompressedDataUrl(file, 600, 0.85);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch { toast.error("No se pudo cargar la foto"); }
    e.target.value = "";
  };
  const addArea = () => {
    const v = areaInput.trim();
    if (!v) return;
    if (form.service_areas.includes(v)) { setAreaInput(""); return; }
    setForm((f) => ({ ...f, service_areas: [...f.service_areas, v] }));
    setAreaInput("");
  };
  const removeArea = (a) => setForm((f) => ({ ...f, service_areas: f.service_areas.filter((x) => x !== a) }));

  const addLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.logos.length >= 2) { toast.error("Máximo 2 logos"); return; }
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Imagen muy grande (máx 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logos: [...f.logos, reader.result] }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const removeLogo = (i) => setForm((f) => ({ ...f, logos: f.logos.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const specs = specialties.map((s) => ({ name: (s.name || "").trim(), cedula: (s.cedula || "").trim() })).filter((s) => s.name);
      const extraCeds = cedulas.map((c) => (c || "").trim()).filter(Boolean);
      const payload = {
        ...form,
        specialties: specs,
        cedulas: extraCeds,
        specialty: specs.map((s) => s.name).join(" · ") || form.specialty,
        specialty_cedula: specs.map((s) => s.cedula).filter(Boolean).join(", "),
      };
      const res = await api.put("/doctor/profile", payload);
      setUser(res.data);
      toast.success("Perfil actualizado");
    } catch (e) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="font-outfit text-3xl font-extrabold text-brand-ink tracking-tight">Mi perfil médico</h1>
        <p className="text-muted-foreground">Estos datos aparecen en tus recetas y estudios en PDF.</p>
      </div>

      {/* Verificación profesional */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="verification-card"
        className="bg-white rounded-3xl p-6 sm:p-7 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
        {vStatus === "verified" ? (
          <div className="flex items-start gap-4" data-testid="verification-verified">
            <div className="h-12 w-12 rounded-2xl bg-teal-500/10 flex items-center justify-center shrink-0">
              <BadgeCheck className="text-teal-600" size={26} />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-lg text-brand-ink flex items-center gap-2">
                Profesional verificado por ClinPed
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full"><ShieldCheck size={12} /> Verificado</span>
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Cédula consultada en el Registro Nacional de Profesionistas. Tu perfil muestra el sello de verificación.</p>
              {user?.verified_at && <p className="text-xs text-stone-400 mt-1">Verificado el {new Date(user.verified_at).toLocaleDateString("es-MX")}</p>}
            </div>
          </div>
        ) : vStatus === "pending" ? (
          <div className="flex items-start gap-4" data-testid="verification-pending">
            <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
              <Clock className="text-amber-600" size={24} />
            </div>
            <div>
              <h2 className="font-outfit font-bold text-lg text-brand-ink">Verificación en proceso</h2>
              <p className="text-sm text-muted-foreground mt-1">Recibimos tu información (cédula <b>{user?.verification_cedula}</b>). Estamos confirmando su registro. Mientras tanto, <b>puedes seguir usando ClinPed con normalidad</b>. Te avisaremos al aprobarla.</p>
            </div>
          </div>
        ) : (
          <div data-testid="verification-form">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={18} className="text-teal-600" />
              <h2 className="font-outfit font-bold text-lg text-brand-ink">Verifica tu profesión</h2>
            </div>
            {vStatus === "rejected" && user?.verification_note && (
              <p className="text-xs text-red-700 bg-red-50 rounded-xl p-2.5 my-2 flex items-start gap-1.5"><AlertCircle size={14} className="mt-0.5 shrink-0" /> {user.verification_note}</p>
            )}
            {vStatus === "correction" && user?.verification_note && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-2.5 my-2 flex items-start gap-1.5"><AlertCircle size={14} className="mt-0.5 shrink-0" /> Corrección solicitada: {user.verification_note}</p>
            )}
            <p className="text-xs text-muted-foreground mb-4">Obtén el sello <b>"Profesional verificado por ClinPed"</b> en tu perfil y en el directorio. Solo consultamos tu <b>número de cédula</b> en el Registro Nacional de Profesionistas (dato público). <b>No pedimos documentos personales</b> y no bloqueamos ninguna función.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Nombre completo (como en la cédula)</Label>
                <Input data-testid="verify-name" value={vForm.name} onChange={(e) => setVForm((f) => ({ ...f, name: e.target.value }))} className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Número de cédula profesional</Label>
                <Input data-testid="verify-cedula" value={vForm.cedula} onChange={(e) => setVForm((f) => ({ ...f, cedula: e.target.value }))} placeholder="0000000" className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Profesión / título</Label>
                <Input data-testid="verify-profession" value={vForm.profession} onChange={(e) => setVForm((f) => ({ ...f, profession: e.target.value }))} placeholder="Médico Cirujano" className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Cédula de especialidad (opcional)</Label>
                <Input data-testid="verify-specialty-cedula" value={vForm.specialty_cedula} onChange={(e) => setVForm((f) => ({ ...f, specialty_cedula: e.target.value }))} className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
              </div>
            </div>
            <button data-testid="submit-verification" onClick={requestVerification} disabled={vSaving}
              className="mt-4 flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-outfit font-semibold px-5 py-2.5 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60">
              <ShieldCheck size={17} /> {vSaving ? "Enviando…" : "Solicitar verificación"}
            </button>
          </div>
        )}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)] space-y-5">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 ring-4 ring-brand-sage/20">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-brand-terracotta text-white text-xl">{user?.name?.[0] || "D"}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-outfit font-bold text-lg text-brand-ink">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([label, k, ph]) => (
            <Field key={k} label={label} k={k} placeholder={ph} value={form[k]}
              onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
          ))}
        </div>
        <div data-testid="specialties-section" className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60 flex items-center gap-1.5"><Stethoscope size={13} /> Especialidad(es)</Label>
            <button type="button" data-testid="add-specialty-btn" onClick={addSpec}
              className="flex items-center gap-1 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-full transition-colors">
              <Plus size={13} /> Agregar especialidad
            </button>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">Elige del catálogo para que el directorio sea homogéneo. Cada especialidad puede llevar su cédula. Usa "Otros" si no aparece.</p>
          {specialties.map((s, i) => {
            const inCatalog = SPECIALTIES.includes(s.name);
            const selectVal = s.other ? "Otros" : (inCatalog ? s.name : (s.name ? "Otros" : ""));
            return (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 bg-stone-50 rounded-2xl p-3" data-testid={`specialty-row-${i}`}>
                <div className="space-y-2">
                  <select data-testid={`specialty-select-${i}`} value={selectVal}
                    onChange={(e) => { const v = e.target.value; if (v === "Otros") setSpecialties((arr) => arr.map((x, idx) => (idx === i ? { ...x, other: true } : x))); else setSpecialties((arr) => arr.map((x, idx) => (idx === i ? { ...x, name: v, other: false } : x))); }}
                    className="w-full px-3 py-2 rounded-xl bg-white border border-stone-200 text-sm outline-none focus:border-teal-500">
                    <option value="">Selecciona especialidad…</option>
                    {SPECIALTIES.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
                  </select>
                  {selectVal === "Otros" && (
                    <Input data-testid={`specialty-other-${i}`} value={inCatalog ? "" : s.name} onChange={(e) => setSpec(i, "name", e.target.value)}
                      placeholder="Escribe la especialidad" className="rounded-xl bg-white border-stone-200" />
                  )}
                  <Input data-testid={`specialty-cedula-${i}`} value={s.cedula} onChange={(e) => setSpec(i, "cedula", e.target.value)}
                    placeholder="Cédula de especialidad (opcional)" className="rounded-xl bg-white border-stone-200" />
                </div>
                {specialties.length > 1 && (
                  <button type="button" data-testid={`remove-specialty-${i}`} onClick={() => removeSpec(i)}
                    className="self-start p-2 rounded-xl text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors" title="Quitar especialidad"><X size={16} /></button>
                )}
              </div>
            );
          })}
        </div>

        <div data-testid="extra-cedulas-section">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Cédulas profesionales adicionales</Label>
            <button type="button" data-testid="add-cedula-btn" onClick={addCed}
              className="flex items-center gap-1 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-full transition-colors">
              <Plus size={13} /> Agregar cédula
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">La cédula profesional principal va arriba. Aquí puedes añadir otras (p. ej. una segunda licenciatura o maestría).</p>
          {cedulas.length > 0 && (
            <div className="space-y-2 mt-2">
              {cedulas.map((c, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`cedula-row-${i}`}>
                  <Input data-testid={`cedula-extra-${i}`} value={c} onChange={(e) => setCed(i, e.target.value)}
                    placeholder="Número de cédula" className="rounded-xl bg-stone-50 border-transparent focus:bg-white flex-1" />
                  <button type="button" data-testid={`remove-cedula-${i}`} onClick={() => removeCed(i)}
                    className="p-2 rounded-xl text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors" title="Quitar cédula"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-brand-sand/20 rounded-2xl p-3 text-xs text-brand-ink/70">
          <b>NOM-004-SSA3-2012:</b> estos datos (nombre, cédulas, universidad, domicilio, teléfono) aparecen en recetas, estudios y consentimientos para cumplir la norma del expediente clínico.
        </div>

        <div data-testid="profile-type-block">
          <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Tipo de perfil</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Define tu tipo de consulta. El perfil <b>Odontológico</b> habilita el módulo dental <b>ClinDent</b> (requiere el plan Odontológico); el perfil <b>Médico</b> no muestra ClinDent.</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["medico", "Médico", "Consulta médica / pediátrica"],
              ["odontologico", "Odontológico", "Incluye ClinDent (odontograma, periodoncia, ortodoncia)"],
            ].map(([val, title, desc]) => (
              <button key={val} type="button" data-testid={`profile-type-${val}`}
                onClick={() => setForm((f) => ({ ...f, profile_type: val }))}
                className={`text-left rounded-2xl border-2 p-3.5 transition-all ${form.profile_type === val ? "border-teal-500 bg-teal-50" : "border-stone-200 bg-white hover:border-stone-300"}`}>
                <p className={`font-outfit font-bold text-sm ${form.profile_type === val ? "text-teal-700" : "text-brand-ink"}`}>{title}</p>
                <p className="text-xs text-brand-ink/60 mt-0.5">{desc}</p>
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Dirección</Label>
          <Textarea data-testid="profile-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Firma electrónica</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Tu firma aparecerá automáticamente en recetas, estudios y consentimientos en PDF.</p>
          <SignaturePad value={form.signature} onChange={(v) => setForm((f) => ({ ...f, signature: v }))} />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Logos del consultorio (máx. 2)</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Aparecerán en las esquinas superiores de tus recetas y estudios al imprimir. Ideal PNG con fondo transparente.</p>
          <div className="flex flex-wrap gap-3 items-center">
            {form.logos.map((lg, i) => (
              <div key={i} className="relative" data-testid={`logo-preview-${i}`}>
                <img src={lg} alt={`logo ${i + 1}`} className="h-16 w-16 object-contain rounded-xl border border-stone-200 bg-white p-1" />
                <button type="button" onClick={() => removeLogo(i)} data-testid={`remove-logo-${i}`}
                  className="absolute -top-2 -right-2 bg-brand-terracotta text-white rounded-full p-1"><X size={12} /></button>
              </div>
            ))}
            {form.logos.length < 2 && (
              <label data-testid="add-logo-btn" className="h-16 w-16 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-stone-300 text-stone-400 hover:border-brand-terracotta hover:text-brand-terracotta cursor-pointer transition-colors">
                <ImagePlus size={18} />
                <input type="file" accept="image/*" onChange={addLogo} className="hidden" />
              </label>
            )}
          </div>
        </div>

        <div className="border-t border-stone-100 pt-5" data-testid="directory-section">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={18} className="text-teal-600" />
            <h2 className="font-outfit font-bold text-lg text-brand-ink">Directorio público de médicos</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Aparece en el directorio para que nuevos pacientes te encuentren y te contacten directamente. Incluido en todos los planes.</p>

          {/* Foto del médico */}
          <div className="flex items-center gap-4 mb-4">
            <Avatar className="h-20 w-20 ring-4 ring-teal-500/20">
              <AvatarImage src={form.photo || user?.picture} />
              <AvatarFallback className="bg-teal-500 text-white text-2xl">{user?.name?.[0] || "D"}</AvatarFallback>
            </Avatar>
            <div className="flex items-center gap-2">
              <label data-testid="upload-doctor-photo" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold bg-stone-50 hover:bg-stone-100 border border-stone-200 rounded-xl px-3 py-2 transition-colors">
                <ImagePlus size={15} /> {form.photo ? "Cambiar foto" : "Subir foto"}
                <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
              </label>
              {form.photo && (
                <button type="button" data-testid="remove-doctor-photo" onClick={() => setForm((f) => ({ ...f, photo: "" }))}
                  className="text-sm text-brand-ink/50 hover:text-red-500 transition-colors">Quitar</button>
              )}
            </div>
          </div>

          {/* Zonas de atención */}
          <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Ciudades / estados donde atiendes</Label>
          <div className="flex gap-2 mt-1.5">
            <div className="relative flex-1">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="area-input" value={areaInput} onChange={(e) => setAreaInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArea(); } }}
                placeholder="Ej. CDMX, Guadalajara…" className="rounded-xl pl-9 bg-stone-50 border-transparent focus:bg-white" />
            </div>
            <button type="button" data-testid="add-area-btn" onClick={addArea}
              className="px-4 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-semibold text-sm transition-colors">Agregar</button>
          </div>
          {form.service_areas.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="area-tags">
              {form.service_areas.map((a) => (
                <span key={a} className="flex items-center gap-1.5 text-xs font-semibold bg-teal-50 text-teal-700 rounded-full px-3 py-1">
                  {a}
                  <button type="button" data-testid={`remove-area-${a}`} onClick={() => removeArea(a)} className="hover:text-red-500"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}

          {/* Bio corta */}
          <div className="mt-4">
            <Label className="text-xs font-bold uppercase tracking-wide text-brand-ink/60">Descripción breve (opcional)</Label>
            <Textarea data-testid="catalog-bio" value={form.catalog_bio} onChange={(e) => setForm({ ...form, catalog_bio: e.target.value })} rows={2}
              maxLength={200} placeholder="Ej. Pediatra certificado, 10 años de experiencia en lactancia y desarrollo infantil." className="rounded-xl mt-1.5 bg-stone-50 border-transparent focus:bg-white" />
          </div>

          {/* Toggle */}
          <label className="flex items-center justify-between gap-3 mt-4 bg-teal-50/60 border border-teal-100 rounded-2xl p-4 cursor-pointer">
            <div>
              <p className="font-semibold text-brand-ink text-sm">Aparecer en el directorio público</p>
              <p className="text-xs text-muted-foreground">Los pacientes podrán verte y solicitarte cita. Necesitas foto y teléfono.</p>
            </div>
            <input type="checkbox" data-testid="public-listed-toggle" checked={form.public_listed}
              onChange={(e) => setForm((f) => ({ ...f, public_listed: e.target.checked }))}
              className="h-6 w-11 appearance-none rounded-full bg-stone-300 checked:bg-teal-500 relative transition-colors cursor-pointer
                before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-5 before:w-5 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-5" />
          </label>

          {/* QR compartible del perfil */}
          {form.public_listed && user?.public_listed && (
            <div className="mt-4 bg-white border border-stone-200 rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-4" data-testid="profile-share-block">
              <div className="bg-white p-2 rounded-xl border-2 border-teal-500/20 shrink-0">
                <QRCodeCanvas value={`${window.location.origin}/directorio?doc=${user.user_id}`} size={120} fgColor="#0D9488" level="M" data-testid="profile-share-qr" />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="font-outfit font-bold text-brand-ink flex items-center gap-1.5 justify-center sm:justify-start"><QrCode size={16} className="text-teal-600" /> Comparte tu perfil</p>
                <p className="text-xs text-muted-foreground mt-1">Coloca este QR en tu consultorio o redes. Al escanearlo, el paciente llega a tu ficha para contactarte y solicitar cita. Solo funciona mientras tu suscripción esté activa.</p>
                <button type="button" data-testid="copy-profile-link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/directorio?doc=${user.user_id}`); toast.success("Enlace copiado"); }}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:underline"><Copy size={14} /> Copiar enlace</button>
                <button type="button" data-testid="download-qr-poster" onClick={() => downloadProfileQrPoster(user, `${window.location.origin}/directorio?doc=${user.user_id}`)}
                  className="mt-2 sm:ml-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-teal-500 hover:bg-teal-600 px-3 py-1.5 rounded-lg transition-colors"><QrCode size={14} /> Descargar QR para imprimir</button>
              </div>
            </div>
          )}
          {form.public_listed && !user?.public_listed && (
            <p className="mt-2 text-xs text-teal-700 bg-teal-50 rounded-xl p-2.5" data-testid="save-to-get-qr">Guarda tu perfil para generar el QR compartible.</p>
          )}
        </div>

        <button data-testid="save-profile" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold px-6 py-3 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60">
          <Save size={18} /> {saving ? "Guardando…" : "Guardar perfil"}
        </button>
      </motion.div>
    </div>
  );
}
